import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Form,
  FormFeedback,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";
import Flatpickr from "react-flatpickr";

import { CreatePromoCodeRequest, ParsedError } from "../../../interfaces";
import { ActivityButton } from "../../../components/Common";
import {
  useGetPromoCodeQuery,
  useAddPromoCodeMutation,
  useUpdatePromoCodeMutation,
} from "../../../store/api/promoCodeApi";
import { ErrorHandlingTypeEnum } from "../../../enums";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const AddEditPromoCodeModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
}) => {
  const { t } = useTranslation();
  const [request] = useState<CreatePromoCodeRequest>();

  //#region api
  const {
    data: dataPromoCode,
    error: errorPromoCode,
    isLoading: isLoadingPromoCode,
  } = useGetPromoCodeQuery(id as string, { skip: !id });

  const promoCode = dataPromoCode?.data || dataPromoCode;

  const [
    addPromoCode,
    { isLoading: isLoadingAdd, error: errorAdd, isError: isErrorAdd },
  ] = useAddPromoCodeMutation();

  const [
    updatePromoCode,
    { isLoading: isLoadingUpdate, error: errorUpdate, isError: isErrorUpdate },
  ] = useUpdatePromoCodeMutation();

  //#endregion

  //#region form
  const form = useFormik<CreatePromoCodeRequest>({
    enableReinitialize: true,
    initialValues: {
      name: {
        en: request?.name.en || "",
        ar: request?.name.ar || "",
      },
      code: request?.code || "",
      discountPercentage: request?.discountPercentage || 0,
      usageLimit: request?.usageLimit || 0,
      validTill: request?.validTill || undefined,
    },
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
      usageLimit: Yup.string().required(
        t("isNotEmpty", {
          displayName: "usageLimit",
        }),
      ),
      code: Yup.string()
        .min(3, t("minLength", { minLength: 3, displayName: "code" }))
        .max(25, t("maxLength", { maxLength: 25, displayName: "code" }))
        .required(
          t("isNotEmpty", {
            displayName: "code",
          }),
        ),
      validTill: Yup.string().nullable(),
      discountPercentage: Yup.number().required(
        t("isNotEmpty", {
          displayName: "discount",
        }),
      ),
    }),
    onSubmit: async (values) => {
      if (id) {
        await updatePromoCode({ id, data: values }).unwrap();
      } else {
        await addPromoCode(values).unwrap();
      }
      onClose();
    },
  });
  //#endregion

  //#region effects
  useEffect(() => {
    if (id && promoCode) {
      form.setValues({
        name: promoCode.name,
        code: promoCode.code,
        discountPercentage: promoCode.discountPercentage,
        usageLimit: promoCode.usageLimit,
        validTill: promoCode?.validTill,
      });
    }
  }, [promoCode, id, isLoadingPromoCode]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error);
    }
  }, [isLoadingAdd, isLoadingUpdate]);

  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
  };

  const onChangeDate = (event: any) => {
    if (event.length > 0) {
      const _validTill = event[0];
      form.setFieldValue("validTill", _validTill);
    }
  };
  //#endregion

  return (
    <Modal
      id="addEdit"
      isOpen={show}
      toggle={() => {
        onClose();
      }}
      centered
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
        action="#"
      >
        <ModalHeader
          className="p-3"
          toggle={() => {
            onClose();
          }}
        >
          {id
            ? t("editItem", { item: "promoCode" })
            : t("addItem", { item: "promoCode" })}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label htmlFor="name.en" className="form-label">
              {t("titleEn")}
              <span className="text-danger">*</span>
            </label>
            <Input
              disabled={!!errorPromoCode}
              autoComplete="off"
              id="name.en"
              name="name.en"
              value={form.values?.name.en}
              type="text"
              className="form-control"
              placeholder={t("enterInEnglish")}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                form.touched.name?.en && form.errors.name?.en ? true : false
              }
            />
            {form.touched.name?.en && form.errors.name?.en ? (
              <FormFeedback type="invalid">
                {form.errors.name?.en as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label htmlFor="name.ar" className="form-label">
              {t("titleAr")}
              <span className="text-danger">*</span>
            </label>
            <Input
              style={{ fontFamily: "aljannat", direction: "rtl" }}
              disabled={!!errorPromoCode}
              autoComplete="off"
              auto
              id="name.ar"
              name="name.ar"
              value={form.values.name?.ar}
              type="text"
              className="form-control"
              placeholder={t("enterInArabic")}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                form.touched.name?.ar && form.errors.name?.ar ? true : false
              }
            />
            {form.touched.name?.ar && form.errors.name?.ar ? (
              <FormFeedback type="invalid">
                {form.errors.name?.ar as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label htmlFor="code" className="form-label">
              {t("code")}
              <span className="text-danger">*</span>
            </label>
            <Input
              disabled={!!errorPromoCode}
              autoComplete="off"
              auto
              autoCapitalize="on"
              id="code"
              name="code"
              value={form.values.code}
              type="text"
              className="form-control"
              placeholder={t("enterPromoCode")}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={form.touched.code && form.errors.code ? true : false}
            />
            {form.touched.code && form.errors.code ? (
              <FormFeedback type="invalid">
                {form.errors.code as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label htmlFor="usageLimit" className="form-label">
              {t("usageLimit")} ({t("zeroMeansUnlimited")})
              <span className="text-danger">*</span>
            </label>
            <Input
              disabled={!!errorPromoCode}
              autoComplete="off"
              auto
              id="usageLimit"
              name="usageLimit"
              value={form.values.usageLimit}
              type="number"
              className="form-control"
              placeholder={t("enterUsageLimit")}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                form.touched.usageLimit && form.errors.usageLimit ? true : false
              }
            />
            {form.touched.usageLimit && form.errors.usageLimit ? (
              <FormFeedback type="invalid">
                {form.errors.usageLimit as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <div>
              <label htmlFor="validTill" className="form-label">
                {t("validTill")} ({t("noDateLifeTime")})
              </label>
            </div>
            <Flatpickr
              id="validTill"
              onChange={(e) => onChangeDate(e)}
              className="form-control"
              value={form.values?.validTill}
              options={{
                mode: "single",
                dateFormat: "Y/m/d",
              }}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="discountPercentage" className="form-label">
              {t("discount")} (%)
            </label>
            <Input
              min={0}
              max={100}
              disabled={!!errorPromoCode}
              autoComplete="off"
              id="discountPercentage"
              name="discountPercentage"
              value={form.values?.discountPercentage}
              type="number"
              className="form-control"
              placeholder={t("enterDiscount")}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                form.touched.discountPercentage &&
                form.errors.discountPercentage
                  ? true
                  : false
              }
            />
            {form.touched.discountPercentage &&
            form.errors.discountPercentage ? (
              <FormFeedback type="invalid">
                {form.errors.discountPercentage as string}
              </FormFeedback>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Link
            to={"#"}
            onClick={onClose}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title="save"
            inProgressTitle="saving"
            color="success"
            className="btn btn-success"
            type="submit"
            inProgress={isLoadingAdd || isLoadingUpdate}
            disabled={isLoadingAdd || isLoadingUpdate || !!errorPromoCode}
          ></ActivityButton>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
